import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

export default function QrReceive() {
  const navigate = useNavigate();
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [qrCode, setQrCode] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generateQrCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        "/api/QrPayment/generate",
        {
          amount: amount ? parseFloat(amount) : null,
          description: description || null,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setQrCode(response.data.qrCode);
      setAccountInfo({
        accountNumber: response.data.accountNumber,
        fullName: response.data.fullName,
        amount: response.data.amount,
        description: response.data.description,
      });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate QR code");
    } finally {
      setLoading(false);
    }
  };

  const downloadQrCode = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (amount) params.append("amount", amount);
      if (description) params.append("description", description);

      const response = await axios.get(
        `/api/QrPayment/download?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "image/png" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `PaymentQR_${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      alert("QR code downloaded successfully!");
    } catch (err) {
      alert("Failed to download QR code");
    }
  };

  const shareQrCode = () => {
    if (navigator.share && qrCode) {
      // Convert base64 to blob for sharing
      fetch(qrCode)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "payment-qr.png", { type: "image/png" });
          navigator
            .share({
              title: "Payment QR Code",
              text: `Send money to ${accountInfo?.fullName}`,
              files: [file],
            })
            .catch((err) => console.log("Share cancelled"));
        });
    } else {
      alert("Sharing not supported on this device");
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1>📱 Receive Payment via QR</h1>
        <button onClick={() => navigate("/dashboard")} style={styles.backButton}>
          ← Back
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Generate Payment QR Code</h2>
        <p style={styles.cardDesc}>
          Create a QR code that others can scan to send you money
        </p>

        <form onSubmit={generateQrCode} style={styles.form}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Amount (Optional)</label>
            <input
              type="number"
              placeholder="Leave empty for any amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
              style={styles.input}
            />
            <small style={styles.hint}>
              If you specify an amount, the payer won't be able to change it
            </small>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Description (Optional)</label>
            <input
              type="text"
              placeholder="e.g., Payment for services, Split bill, etc."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={styles.input}
            />
          </div>

          <button type="submit" style={styles.generateButton} disabled={loading}>
            {loading ? "Generating..." : "🔄 Generate QR Code"}
          </button>
        </form>
      </div>

      {qrCode && (
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Your Payment QR Code</h2>

          <div style={styles.qrContainer}>
            <img src={qrCode} alt="Payment QR Code" style={styles.qrImage} />
          </div>

          <div style={styles.infoBox}>
            <h3 style={styles.infoTitle}>Payment Details:</h3>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Account:</span>
              <span style={styles.infoValue}>{accountInfo?.accountNumber}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Name:</span>
              <span style={styles.infoValue}>{accountInfo?.fullName}</span>
            </div>
            {accountInfo?.amount && (
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Amount:</span>
                <span style={{...styles.infoValue, color: "#28a745", fontWeight: "bold"}}>
                  ₦{accountInfo.amount.toLocaleString()}
                </span>
              </div>
            )}
            {accountInfo?.description && (
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Description:</span>
                <span style={styles.infoValue}>{accountInfo.description}</span>
              </div>
            )}
          </div>

          <div style={styles.actionButtons}>
            <button onClick={downloadQrCode} style={{...styles.button, ...styles.downloadBtn}}>
              📥 Download
            </button>
            <button onClick={shareQrCode} style={{...styles.button, ...styles.shareBtn}}>
              📤 Share
            </button>
          </div>

          <p style={styles.instruction}>
            💡 Show this QR code to the person who wants to pay you, or share it via WhatsApp, email, etc.
          </p>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: "600px",
    margin: "0 auto",
    padding: "20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "30px",
  },
  backButton: {
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    backgroundColor: "#6c757d",
    color: "white",
  },
  error: {
    backgroundColor: "#fee",
    color: "#c33",
    padding: "15px",
    borderRadius: "6px",
    marginBottom: "20px",
    border: "1px solid #fcc",
  },
  card: {
    backgroundColor: "white",
    padding: "30px",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    marginBottom: "20px",
  },
  cardTitle: {
    fontSize: "22px",
    marginBottom: "10px",
    color: "#333",
  },
  cardDesc: {
    color: "#666",
    marginBottom: "20px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  inputGroup: {
    marginBottom: "20px",
  },
  label: {
    display: "block",
    marginBottom: "8px",
    fontWeight: "500",
    color: "#333",
  },
  input: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "1px solid #ddd",
    borderRadius: "4px",
    boxSizing: "border-box",
  },
  hint: {
    display: "block",
    marginTop: "5px",
    fontSize: "12px",
    color: "#666",
    fontStyle: "italic",
  },
  generateButton: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    backgroundColor: "#007bff",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    padding: "20px",
    backgroundColor: "#f8f9fa",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  qrImage: {
    maxWidth: "300px",
    width: "100%",
    height: "auto",
  },
  infoBox: {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    marginBottom: "20px",
  },
  infoTitle: {
    fontSize: "16px",
    fontWeight: "600",
    marginBottom: "15px",
    color: "#333",
  },
  infoRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #dee2e6",
  },
  infoLabel: {
    fontWeight: "500",
    color: "#666",
  },
  infoValue: {
    color: "#333",
  },
  actionButtons: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px",
    marginBottom: "20px",
  },
  button: {
    padding: "12px",
    fontSize: "14px",
    fontWeight: "600",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
  },
  downloadBtn: {
    backgroundColor: "#28a745",
    color: "white",
  },
  shareBtn: {
    backgroundColor: "#007bff",
    color: "white",
  },
  instruction: {
    textAlign: "center",
    color: "#666",
    fontSize: "14px",
    fontStyle: "italic",
  },
};

